import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { RolePill } from "@/components/common";
import { LogOut, LayoutDashboard, PlusCircle, ListChecks, ClipboardCheck, UserCircle2, Building2, Users } from "lucide-react";

const linkClass = ({ isActive }) =>
  `inline-flex items-center gap-1.5 text-sm tracking-tight px-3 py-1.5 rounded-lg transition-colors ${
    isActive ? "bg-zinc-900 text-white" : "text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100"
  }`;

export default function TopNav() {
  const { user, organization, logout } = useAuth();
  const nav = useNavigate();
  if (!user) return null;
  const isManager = user.role === "manager";

  const initials = (user.name || user.email || "U")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleLogout = () => {
    logout();
    nav("/login", { replace: true });
  };

  return (
    <header className="sticky top-0 z-50 nav-glass border-b border-zinc-200">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/dashboard" data-testid="brand-link" className="flex items-center gap-2 group shrink-0">
            <div className="w-7 h-7 rounded-md bg-zinc-950 text-white flex items-center justify-center font-semibold text-[13px] tracking-tight">
              L
            </div>
            <span className="font-display text-[17px] font-semibold tracking-tight text-zinc-950">Ledgerly</span>
          </Link>
          {organization ? (
            <Link
              to={isManager ? "/team" : "/profile"}
              data-testid="nav-org-chip"
              className="hidden md:inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-zinc-200 bg-zinc-50 text-zinc-700 text-xs hover:bg-zinc-100 max-w-[200px]"
              title={organization.domain}
            >
              <Building2 className="w-3 h-3 shrink-0" />
              <span className="truncate font-medium">{organization.name}</span>
            </Link>
          ) : null}
        </div>

        <nav className="flex items-center gap-1">
          <NavLink to="/dashboard" className={linkClass} data-testid="nav-dashboard">
            <LayoutDashboard className="w-4 h-4" /> Dashboard
          </NavLink>
          <NavLink to="/add" className={linkClass} data-testid="nav-add">
            <PlusCircle className="w-4 h-4" /> New
          </NavLink>
          <NavLink to="/expenses" className={linkClass} data-testid="nav-expenses">
            <ListChecks className="w-4 h-4" /> Expenses
          </NavLink>
          {isManager ? (
            <NavLink to="/approvals" className={linkClass} data-testid="nav-approvals">
              <ClipboardCheck className="w-4 h-4" /> Approvals
            </NavLink>
          ) : null}
          {isManager ? (
            <NavLink to="/team" className={linkClass} data-testid="nav-team">
              <Users className="w-4 h-4" /> Team
            </NavLink>
          ) : null}
          <NavLink to="/profile" className={linkClass} data-testid="nav-profile">
            <UserCircle2 className="w-4 h-4" /> Profile
          </NavLink>
        </nav>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-zinc-900 text-white flex items-center justify-center text-xs font-semibold">
              {initials}
            </div>
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="text-sm font-medium text-zinc-900" data-testid="nav-user-name">{user.name}</span>
              <div className="flex items-center gap-1.5 mt-0.5">
                <RolePill role={user.role} />
              </div>
            </div>
          </div>
          <button
            data-testid="nav-signout-btn"
            onClick={handleLogout}
            title="Sign out"
            className="p-2 rounded-lg text-zinc-500 hover:text-zinc-950 hover:bg-zinc-100 transition-colors"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
}
